
import React from 'react';
import { JobActivityLog, JobActivityType } from '@/types/activity';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Briefcase, Edit3, FileText, Users, UserPlus, CheckCircle, Info, HelpCircle, FilePlus, ListFilter, MessageSquare, Star, Eye
} from 'lucide-react';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';

interface ActivityLogItemProps {
  activity: JobActivityLog;
}

const getActivityIcon = (type: JobActivityType) => {
  const iconProps = { className: "h-5 w-5 text-muted-foreground" };
  switch (type) {
    case 'job_created': return <FilePlus {...iconProps} />;
    case 'job_updated': return <Edit3 {...iconProps} />;
    case 'job_status_changed': return <ListFilter {...iconProps} />;
    case 'application_submitted': return <FileText {...iconProps} />;
    case 'application_status_changed': return <Users {...iconProps} />;
    case 'candidate_shortlisted': return <Star {...iconProps} />;
    case 'note_added_to_job':
    case 'note_added_to_application':
      return <MessageSquare {...iconProps} />;
    default: return <Info {...iconProps} />;
  }
};

const formatActivityDetails = (activity: JobActivityLog): string => {
  const { activity_type, details } = activity;
  let message = activity_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  if (details) {
    if (details.old_status && details.new_status) {
      message += `: Status changed from '${details.old_status}' to '${details.new_status}'`;
    } else if (details.new_status) {
      message += `: Status set to '${details.new_status}'`;
    }
    if (details.candidate_name) {
      message += ` for ${details.candidate_name}`;
    }
    if (details.note_preview) {
       message += `: "${details.note_preview}..."`;
    }
    if (details.updated_fields) {
      message += `: Updated fields - ${Object.keys(details.updated_fields).join(', ')}`;
    }
     if (details.title) {
      message += `: ${details.title}`;
    }
  }
  return message;
};

export const ActivityLogItem: React.FC<ActivityLogItemProps> = ({ activity }) => {
  const userName = activity.user ? `${activity.user.first_name || ''} ${activity.user.last_name || ''}`.trim() || activity.user.email : 'System';
  const userInitial = userName?.substring(0, 1).toUpperCase() || 'S';

  return (
    <div className="flex items-start space-x-4 py-4">
      <Avatar className="h-10 w-10">
        {/* Placeholder for user avatar image if available */}
        {/* <AvatarImage src={activity.user?.avatar_url} alt={userName} /> */}
        <AvatarFallback>{userInitial}</AvatarFallback>
      </Avatar>
      <div className="flex-1 space-y-1">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium leading-none">
            {userName}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatDistanceToNowStrict(parseISO(activity.activity_timestamp), { addSuffix: true })}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {getActivityIcon(activity.activity_type)}
          <p className="text-sm text-muted-foreground">{formatActivityDetails(activity)}</p>
        </div>
      </div>
    </div>
  );
};
